import { CONFIG } from '../config/config.js';

// Expected format: EMBARC_MBR_<employerGroup>_P_F_yyyymmdd.csv
const FILE_NAMING_CONVENTION_REGEX = /^EMBARC_MBR_.+_P_F_\d{8}\.csv$/i;

/**
 * Validates the file naming convention: EMBARC_MBR_*_P_F_yyyymmdd.csv
 * @param {string} fileName
 * @returns {{ isValid: boolean, error?: string }}
 */
function validateFileNaming(fileName) {
  if (!FILE_NAMING_CONVENTION_REGEX.test(fileName)) {
    return {
      isValid: false,
      error: 'File name does not match the required naming convention: EMBARC_MBR_*_P_F_yyyymmdd.csv, where * represents the employer group number.'
    };
  }

  // Extract and validate the yyyymmdd date portion (8 chars, 12 from end before .csv)
  const datePart = fileName.slice(-12, -4); // e.g. "20240101"
  const year  = parseInt(datePart.slice(0, 4), 10);
  const month = parseInt(datePart.slice(4, 6), 10);
  const day   = parseInt(datePart.slice(6, 8), 10);
  const date  = new Date(year, month - 1, day);

  if (
    isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return {
      isValid: false,
      error: `Invalid date format in file name. Expected yyyymmdd, got: ${datePart}`
    };
  }

  return { isValid: true };
}

/**
 * Validates a CSV file on the client side (instance, size, naming convention).
 * Server-side content validation is handled during the upload itself.
 * @param {File} file - The CSV file to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateCsvFile(file) {
  // Must be a File instance
  if (!file || !(file instanceof File)) {
    return { isValid: false, error: 'Invalid file provided. Please select a valid CSV file.' };
  }

  // Empty file check
  if (file.size === 0) {
    return { isValid: false, error: 'No file provided or file is empty.' };
  }

  // Path traversal check — ".." can appear in a filename and would pass the naming regex (.+ matches it)
  if (file.name.includes('..')) {
    return { isValid: false, error: 'Filename contains invalid characters or path traversal patterns.' };
  }

  // Naming convention: EMBARC_MBR_*_P_F_yyyymmdd.csv
  // Also implicitly enforces non-blank filename and .csv extension.
  const namingResult = validateFileNaming(file.name);
  if (!namingResult.isValid) {
    return namingResult;
  }

  // File size limit
  if (file.size > CONFIG.MAX_FILE_SIZE) {
    return { isValid: false, error: `File size too large. Maximum file size allowed is ${CONFIG.MAX_FILE_SIZE_GB}GB.` };
  }

  return { isValid: true };
}
